/**
 * Payroll API
 *
 * Base path : /api/hr/payroll
 * Auth      : Bearer token via shared api-client interceptor
 * Backend guide: 2026-07-15 (brand-new module, see memory/project_payroll_module_backend_guide.md)
 */

import { api } from "#/lib/api-client";
import type {
  BenefitCategory,
  BenefitCategoryRequest,
  CreateStatutoryConfigRequest,
  DeductionCategory,
  DeductionCategoryRequest,
  EmployeeBankAccount,
  EmployeeBankAccountRequest,
  EmployeeBenefit,
  EmployeeBenefitRequest,
  EmployeeBenefitUpdateRequest,
  EmployeeDeduction,
  EmployeeDeductionRequest,
  EmployeeDeductionUpdateRequest,
  MissingBankAccount,
  PageResponse,
  Payslip,
  PayrollImportBatch,
  PayrollImportRow,
  PayrollPeriod,
  SalaryRecord,
  SalaryRecordUpdateRequest,
  StatutoryConfig,
  UpdateStatutoryConfigRequest,
} from "./types";

const BASE = "/api/hr/payroll";

/** Build a query string, omitting undefined / null / empty-string values */
function q(params: Record<string, unknown>): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
  }
  return qs.toString();
}

/** Normalise a "list all" response that may come back as a bare array or Spring-Page-wrapped
 *  ({ content: [...] }) or envelope-wrapped ({ data: [...] }). Deduction Categories turned out
 *  to return a Page wrapper rather than a bare array (the "data.map is not a function" bug) —
 *  applied defensively to every other endpoint typed as returning T[] so the same class of bug
 *  doesn't resurface elsewhere without a live-backend check confirming the actual shape. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function unwrapArray<T>(raw: any): T[] {
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.content)) return raw.content;
  if (Array.isArray(raw?.data)) return raw.data;
  return [];
}

/** Normalise Spring Page response — handles nested page metadata + envelope wrapping */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normPage<T>(raw: any): PageResponse<T> {
  const body = raw?.content !== undefined ? raw : (raw?.data ?? raw);
  const meta = body?.page ?? body;
  return {
    content: body?.content ?? [],
    totalElements:
      meta?.totalElements ?? meta?.total_elements ?? meta?.total ?? 0,
    totalPages: meta?.totalPages ?? meta?.total_pages ?? 0,
    page: meta?.number ?? meta?.page ?? 0,
    size: meta?.size ?? 0,
  };
}

export type PeriodListParams = {
  status?: string;
  year?: number;
  page?: number;
  size?: number;
};

export const payrollApi = {
  // ── Periods ───────────────────────────────────────────────────────────────

  /** POST /periods?year&month — MANAGE */
  async createPeriod(year: number, month: number): Promise<PayrollPeriod> {
    return (
      await api.post<PayrollPeriod>(`${BASE}/periods?${q({ year, month })}`)
    ).data;
  },

  /** GET /periods?status&year&page&size — VIEW/MANAGE/APPROVE */
  async listPeriods(
    page = 0,
    size = 12,
    params?: PeriodListParams,
  ): Promise<PageResponse<PayrollPeriod>> {
    const qs = q({ page, size, ...params });
    const res = await api.get(`${BASE}/periods?${qs}`);
    return normPage(res.data);
  },

  /** GET /periods/{id} */
  async getPeriod(id: number): Promise<PayrollPeriod> {
    return (await api.get<PayrollPeriod>(`${BASE}/periods/${id}`)).data;
  },

  /** POST /periods/{id}/calculate — PROCESS/MANAGE, safe to re-run while IMPORTED/CALCULATED/REJECTED */
  async calculatePeriod(id: number): Promise<PayrollPeriod> {
    return (await api.post<PayrollPeriod>(`${BASE}/periods/${id}/calculate`))
      .data;
  },

  /** POST /periods/{id}/submit — PROCESS/MANAGE */
  async submitPeriod(id: number): Promise<PayrollPeriod> {
    return (await api.post<PayrollPeriod>(`${BASE}/periods/${id}/submit`)).data;
  },

  /** POST /periods/{id}/approve — APPROVE */
  async approvePeriod(id: number): Promise<PayrollPeriod> {
    return (await api.post<PayrollPeriod>(`${BASE}/periods/${id}/approve`))
      .data;
  },

  /** POST /periods/{id}/reject { reason } — APPROVE */
  async rejectPeriod(id: number, reason: string): Promise<PayrollPeriod> {
    return (
      await api.post<PayrollPeriod>(`${BASE}/periods/${id}/reject`, { reason })
    ).data;
  },

  /** POST /periods/{id}/reopen — MANAGE, REJECTED -> CALCULATED */
  async reopenPeriod(id: number): Promise<PayrollPeriod> {
    return (await api.post<PayrollPeriod>(`${BASE}/periods/${id}/reopen`)).data;
  },

  /** POST /periods/{id}/mark-paid — PROCESS/MANAGE, APPROVED -> PAID */
  async markPaid(id: number): Promise<PayrollPeriod> {
    return (await api.post<PayrollPeriod>(`${BASE}/periods/${id}/mark-paid`))
      .data;
  },

  /** GET /periods/{id}/missing-bank-accounts — PROCESS/MANAGE. Employees a Calculate attempt
   *  flagged as missing complete bank details — lets the UI offer an inline fix. */
  async getMissingBankAccounts(id: number): Promise<MissingBankAccount[]> {
    const res = await api.get(`${BASE}/periods/${id}/missing-bank-accounts`);
    return unwrapArray<MissingBankAccount>(res.data);
  },

  // ── Salary records (imported data — editable pre-submission only) ──────────

  /** GET /periods/{periodId}/salary-records — VIEW/MANAGE */
  async listSalaryRecords(periodId: number): Promise<SalaryRecord[]> {
    const res = await api.get(`${BASE}/periods/${periodId}/salary-records`);
    return unwrapArray<SalaryRecord>(res.data);
  },

  /** PATCH /periods/{periodId}/salary-records/{employeeId} — PROCESS/MANAGE,
   *  rejected once the period reaches PENDING_APPROVAL or later */
  async updateSalaryRecord(
    periodId: number,
    employeeId: number,
    payload: SalaryRecordUpdateRequest,
  ): Promise<SalaryRecord> {
    return (
      await api.patch<SalaryRecord>(
        `${BASE}/periods/${periodId}/salary-records/${employeeId}`,
        payload,
      )
    ).data;
  },

  /** DELETE /periods/{periodId}/salary-records/{employeeId} — "revert" an imported row —
   *  PROCESS/MANAGE, rejected once the period reaches PENDING_APPROVAL or later */
  async deleteSalaryRecord(periodId: number, employeeId: number): Promise<void> {
    await api.delete(`${BASE}/periods/${periodId}/salary-records/${employeeId}`);
  },

  // ── Import ────────────────────────────────────────────────────────────────

  /** GET /import/template — PROCESS/MANAGE — pre-filled xlsx blob */
  async downloadImportTemplate(): Promise<Blob> {
    const r = await api.get(`${BASE}/import/template`, {
      responseType: "blob",
    });
    return r.data;
  },

  /** POST /periods/{periodId}/import multipart file — PROCESS/MANAGE
   *  Content-Type is explicitly set to undefined so the browser auto-sets
   *  multipart/form-data with the correct boundary. A global application/json
   *  default would otherwise cause a 415 Unsupported Media Type. */
  async uploadImport(
    periodId: number,
    file: File,
  ): Promise<PayrollImportBatch> {
    const form = new FormData();
    form.append("file", file);
    return (
      await api.post<PayrollImportBatch>(
        `${BASE}/periods/${periodId}/import`,
        form,
        { headers: { "Content-Type": undefined } },
      )
    ).data;
  },

  /** POST /import/{batchId}/post — PROCESS/MANAGE — commits valid rows, auto-triggers calculation */
  async postImportBatch(batchId: number): Promise<PayrollImportBatch> {
    return (
      await api.post<PayrollImportBatch>(`${BASE}/import/${batchId}/post`)
    ).data;
  },

  /** GET /periods/{periodId}/import?page&size — VIEW/MANAGE — batch list for a period */
  async listImportBatches(
    periodId: number,
    page = 0,
    size = 10,
  ): Promise<PageResponse<PayrollImportBatch>> {
    const res = await api.get(
      `${BASE}/periods/${periodId}/import?${q({ page, size })}`,
    );
    return normPage(res.data);
  },

  /** GET /import/{batchId}/rows?onlyErrors&page&size — VIEW/MANAGE */
  async listImportRows(
    batchId: number,
    params?: { onlyErrors?: boolean; page?: number; size?: number },
  ): Promise<PageResponse<PayrollImportRow>> {
    const qs = q({
      page: params?.page ?? 0,
      size: params?.size ?? 50,
      onlyErrors: params?.onlyErrors,
    });
    const res = await api.get(`${BASE}/import/${batchId}/rows?${qs}`);
    return normPage(res.data);
  },

  // ── Statutory config ──────────────────────────────────────────────────────

  /** POST /config/statutory — MANAGE */
  async createStatutoryConfig(
    payload: CreateStatutoryConfigRequest,
  ): Promise<StatutoryConfig> {
    return (
      await api.post<StatutoryConfig>(`${BASE}/config/statutory`, payload)
    ).data;
  },

  /** PUT /config/statutory/{id} — MANAGE, partial update, null fields left unchanged */
  async updateStatutoryConfig(
    id: number,
    payload: UpdateStatutoryConfigRequest,
  ): Promise<StatutoryConfig> {
    return (
      await api.put<StatutoryConfig>(`${BASE}/config/statutory/${id}`, payload)
    ).data;
  },

  /** POST /config/statutory/{id}/active?active= — MANAGE */
  async setStatutoryConfigActive(
    id: number,
    active: boolean,
  ): Promise<StatutoryConfig> {
    return (
      await api.post<StatutoryConfig>(
        `${BASE}/config/statutory/${id}/active?active=${active}`,
      )
    ).data;
  },

  /** GET /config/statutory — VIEW/MANAGE, newest first */
  async listStatutoryConfigs(): Promise<StatutoryConfig[]> {
    const res = await api.get(`${BASE}/config/statutory`);
    return unwrapArray<StatutoryConfig>(res.data);
  },

  /** GET /config/statutory/{id} — VIEW/MANAGE */
  async getStatutoryConfig(id: number): Promise<StatutoryConfig> {
    return (await api.get<StatutoryConfig>(`${BASE}/config/statutory/${id}`))
      .data;
  },

  /** GET /config/statutory/effective?date=yyyy-MM-dd — VIEW/MANAGE — preview which config applies */
  async getEffectiveStatutoryConfig(date: string): Promise<StatutoryConfig> {
    return (
      await api.get<StatutoryConfig>(
        `${BASE}/config/statutory/effective?${q({ date })}`,
      )
    ).data;
  },

  // ── Deduction categories ──────────────────────────────────────────────────

  /** GET /deduction-categories — VIEW/MANAGE — backend returns a Spring Page wrapper
   *  ({ content: [...], page: {...} }), not a bare array. */
  async listDeductionCategories(): Promise<DeductionCategory[]> {
    const res = await api.get(`${BASE}/deduction-categories`);
    return unwrapArray<DeductionCategory>(res.data);
  },

  /** GET /deduction-categories/{id} — VIEW/MANAGE */
  async getDeductionCategory(id: number): Promise<DeductionCategory> {
    return (
      await api.get<DeductionCategory>(`${BASE}/deduction-categories/${id}`)
    ).data;
  },

  /** POST /deduction-categories — MANAGE */
  async createDeductionCategory(
    payload: DeductionCategoryRequest,
  ): Promise<DeductionCategory> {
    return (
      await api.post<DeductionCategory>(`${BASE}/deduction-categories`, payload)
    ).data;
  },

  /** PUT /deduction-categories/{id} — MANAGE */
  async updateDeductionCategory(
    id: number,
    payload: Partial<DeductionCategoryRequest>,
  ): Promise<DeductionCategory> {
    return (
      await api.put<DeductionCategory>(
        `${BASE}/deduction-categories/${id}`,
        payload,
      )
    ).data;
  },

  // ── Employee deductions ───────────────────────────────────────────────────

  /** POST /employee-deductions — MANAGE */
  async createEmployeeDeduction(
    payload: EmployeeDeductionRequest,
  ): Promise<EmployeeDeduction> {
    return (
      await api.post<EmployeeDeduction>(`${BASE}/employee-deductions`, payload)
    ).data;
  },

  /** PUT /employee-deductions/{id} — MANAGE */
  async updateEmployeeDeduction(
    id: number,
    payload: EmployeeDeductionUpdateRequest,
  ): Promise<EmployeeDeduction> {
    return (
      await api.put<EmployeeDeduction>(
        `${BASE}/employee-deductions/${id}`,
        payload,
      )
    ).data;
  },

  /** POST /employee-deductions/{id}/active?active= — MANAGE */
  async setEmployeeDeductionActive(
    id: number,
    active: boolean,
  ): Promise<EmployeeDeduction> {
    return (
      await api.post<EmployeeDeduction>(
        `${BASE}/employee-deductions/${id}/active?active=${active}`,
      )
    ).data;
  },

  /** GET /employee-deductions/employee/{employeeId}?page&size — VIEW/MANAGE */
  async listEmployeeDeductions(
    employeeId: number,
    page = 0,
    size = 20,
  ): Promise<PageResponse<EmployeeDeduction>> {
    const res = await api.get(
      `${BASE}/employee-deductions/employee/${employeeId}?${q({ page, size })}`,
    );
    return normPage(res.data);
  },

  // ── Benefit categories (mirrors deduction categories — added to gross pay, not subtracted) ──

  /** GET /benefit-categories — VIEW/MANAGE — backend returns a Spring Page wrapper
   *  ({ content: [...], page: {...} }), not a bare array. */
  async listBenefitCategories(): Promise<BenefitCategory[]> {
    const res = await api.get(`${BASE}/benefit-categories`);
    return unwrapArray<BenefitCategory>(res.data);
  },

  /** GET /benefit-categories/{id} — VIEW/MANAGE */
  async getBenefitCategory(id: number): Promise<BenefitCategory> {
    return (
      await api.get<BenefitCategory>(`${BASE}/benefit-categories/${id}`)
    ).data;
  },

  /** POST /benefit-categories — MANAGE */
  async createBenefitCategory(
    payload: BenefitCategoryRequest,
  ): Promise<BenefitCategory> {
    return (
      await api.post<BenefitCategory>(`${BASE}/benefit-categories`, payload)
    ).data;
  },

  /** PUT /benefit-categories/{id} — MANAGE */
  async updateBenefitCategory(
    id: number,
    payload: Partial<BenefitCategoryRequest>,
  ): Promise<BenefitCategory> {
    return (
      await api.put<BenefitCategory>(
        `${BASE}/benefit-categories/${id}`,
        payload,
      )
    ).data;
  },

  // ── Employee benefits ─────────────────────────────────────────────────────

  /** POST /employee-benefits — MANAGE */
  async createEmployeeBenefit(
    payload: EmployeeBenefitRequest,
  ): Promise<EmployeeBenefit> {
    return (
      await api.post<EmployeeBenefit>(`${BASE}/employee-benefits`, payload)
    ).data;
  },

  /** PUT /employee-benefits/{id} — MANAGE */
  async updateEmployeeBenefit(
    id: number,
    payload: EmployeeBenefitUpdateRequest,
  ): Promise<EmployeeBenefit> {
    return (
      await api.put<EmployeeBenefit>(
        `${BASE}/employee-benefits/${id}`,
        payload,
      )
    ).data;
  },

  /** POST /employee-benefits/{id}/active?active= — MANAGE */
  async setEmployeeBenefitActive(
    id: number,
    active: boolean,
  ): Promise<EmployeeBenefit> {
    return (
      await api.post<EmployeeBenefit>(
        `${BASE}/employee-benefits/${id}/active?active=${active}`,
      )
    ).data;
  },

  /** GET /employee-benefits/employee/{employeeId}?page&size — VIEW/MANAGE */
  async listEmployeeBenefits(
    employeeId: number,
    page = 0,
    size = 20,
  ): Promise<PageResponse<EmployeeBenefit>> {
    const res = await api.get(
      `${BASE}/employee-benefits/employee/${employeeId}?${q({ page, size })}`,
    );
    return normPage(res.data);
  },

  // ── Employee bank account ─────────────────────────────────────────────────

  /** GET /employees/{employeeId}/bank-account — self-or-(VIEW/MANAGE) */
  async getBankAccount(employeeId: number): Promise<EmployeeBankAccount> {
    return (
      await api.get<EmployeeBankAccount>(
        `${BASE}/employees/${employeeId}/bank-account`,
      )
    ).data;
  },

  /** PUT /employees/{employeeId}/bank-account — MANAGE only */
  async updateBankAccount(
    employeeId: number,
    payload: EmployeeBankAccountRequest,
  ): Promise<EmployeeBankAccount> {
    return (
      await api.put<EmployeeBankAccount>(
        `${BASE}/employees/${employeeId}/bank-account`,
        payload,
      )
    ).data;
  },

  // ── Payslips ───────────────────────────────────────────────────────────────

  /** GET /payslips/mine?page&size — self */
  async myPayslips(page = 0, size = 12): Promise<PageResponse<Payslip>> {
    const res = await api.get(`${BASE}/payslips/mine?${q({ page, size })}`);
    return normPage(res.data);
  },

  /** GET /payslips/mine/period/{periodId} — self */
  async myPayslipForPeriod(periodId: number): Promise<Payslip> {
    return (await api.get<Payslip>(`${BASE}/payslips/mine/period/${periodId}`))
      .data;
  },

  /** GET /payslips/mine/period/{periodId}/pdf — self — pdf blob */
  async myPayslipPdf(periodId: number): Promise<Blob> {
    const r = await api.get(`${BASE}/payslips/mine/period/${periodId}/pdf`, {
      responseType: "blob",
    });
    return r.data;
  },

  /** GET /payslips/{id} — self-or-(VIEW/MANAGE) */
  async getPayslip(id: number): Promise<Payslip> {
    return (await api.get<Payslip>(`${BASE}/payslips/${id}`)).data;
  },

  /** GET /payslips/{id}/pdf — self-or-(VIEW/MANAGE) — pdf blob */
  async payslipPdf(id: number): Promise<Blob> {
    const r = await api.get(`${BASE}/payslips/${id}/pdf`, {
      responseType: "blob",
    });
    return r.data;
  },

  /** GET /payslips/period/{periodId} — VIEW/MANAGE — every payslip in a period */
  async payslipsForPeriod(periodId: number): Promise<Payslip[]> {
    const res = await api.get(`${BASE}/payslips/period/${periodId}`);
    return unwrapArray<Payslip>(res.data);
  },

  // ── Reports ────────────────────────────────────────────────────────────────

  /** GET /reports/periods/{id}/summary.xlsx | .pdf — VIEW/MANAGE */
  async periodSummaryReport(id: number, format: "xlsx" | "pdf"): Promise<Blob> {
    const r = await api.get(`${BASE}/reports/periods/${id}/summary.${format}`, {
      responseType: "blob",
    });
    return r.data;
  },

  /** GET /reports/periods/{id}/detail.xlsx | .pdf — VIEW/MANAGE */
  async periodDetailReport(id: number, format: "xlsx" | "pdf"): Promise<Blob> {
    const r = await api.get(`${BASE}/reports/periods/${id}/detail.${format}`, {
      responseType: "blob",
    });
    return r.data;
  },

  /** GET /reports/employees/{id}/p9/{year}.pdf | .xlsx — self-or-(VIEW/MANAGE) */
  async employeeP9(
    employeeId: number,
    year: number,
    format: "pdf" | "xlsx",
  ): Promise<Blob> {
    const r = await api.get(
      `${BASE}/reports/employees/${employeeId}/p9/${year}.${format}`,
      { responseType: "blob" },
    );
    return r.data;
  },

  /** GET /reports/remittance.xlsx | .pdf — VIEW/MANAGE only. `category` is either a statutory
   *  code (PAYE/NSSF/SHIF/HOUSING_LEVY) or a PayrollDeductionCategory's `code` (sacco, bank
   *  loan, ...). from/to select the accounting range — a single period for "monthly", Jan-Dec
   *  of one year for "yearly", or any custom range in between. */
  async remittanceReport(
    category: string,
    fromYear: number, fromMonth: number, toYear: number, toMonth: number,
    format: "xlsx" | "pdf",
  ): Promise<Blob> {
    const r = await api.get(`${BASE}/reports/remittance.${format}`, {
      params: { category, fromYear, fromMonth, toYear, toMonth },
      responseType: "blob",
    });
    return r.data;
  },

  /** GET /reports/employees/{id}/statement.xlsx | .pdf — self-or-(VIEW/MANAGE). One row per
   *  period across the given accounting range — the earnings/deductions/net-pay history for
   *  that employee, not just a single period's payslip. */
  async employeeStatement(
    employeeId: number,
    fromYear: number, fromMonth: number, toYear: number, toMonth: number,
    format: "xlsx" | "pdf",
  ): Promise<Blob> {
    const r = await api.get(`${BASE}/reports/employees/${employeeId}/statement.${format}`, {
      params: { fromYear, fromMonth, toYear, toMonth },
      responseType: "blob",
    });
    return r.data;
  },
};

/** Trigger a browser download for an already-fetched Blob via a short-lived in-memory
 *  <a> element — never expose the authenticated API URL directly (see memory:
 *  feedback_file_downloads_must_use_authenticated_client). */
export function downloadBlob(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(url);
}